// AdminInventory: Manage inventory transactions and adjust stock levels
import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Button,
	TextField,
	Select,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Chip,
	Alert,
	InputAdornment,
	TableSortLabel,
} from "@mui/material";
import { Search, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import supabase from "../../../supabaseClient";

const TRANSACTION_TYPES = [
	"sale",
	"adjustment",
	"purchase",
	"waste",
	"transfer",
];

const stockChip = (qty, reorderLevel) => {
	if (qty === 0)
		return <Chip label="Out of Stock" color="error" size="small" />;
	if (qty <= reorderLevel)
		return <Chip label="Low Stock" color="warning" size="small" />;
	return <Chip label="In Stock" color="success" size="small" />;
};

export default function AdminInventory() {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");
	const [sortConfig, setSortConfig] = useState({
		key: "title",
		direction: "asc",
	});
	const [txDialogOpen, setTxDialogOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);
	const [txForm, setTxForm] = useState({
		transaction_type: "adjustment",
		quantity_change: "",
		notes: "",
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [historyItem, setHistoryItem] = useState(null);
	const [history, setHistory] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [zeroOutAlerts, setZeroOutAlerts] = useState([]);

	const removeZeroOutAlert = (alertId) => {
		setZeroOutAlerts((current) =>
			current.filter((alert) => alert.id !== alertId),
		);
	};

	const undoZeroOut = async (alert) => {
		setError("");
		removeZeroOutAlert(alert.id);

		const item = items.find((entry) => entry.id === alert.itemId);
		if (!item) return;
		const restoredQty = Number(alert.previousQty ?? 0);
		const { error: updateErr } = await supabase
			.from("store_items")
			.update({ quantity_on_hand: restoredQty })
			.eq("id", item.id);
		if (updateErr) {
			setError(updateErr.message);
			return;
		}

		const {
			data: { user },
		} = await supabase.auth.getUser();
		const { error: txErr } = await supabase
			.from("inventory_transactions")
			.insert([
				{
					store_item_id: item.id,
					transaction_type: "adjustment",
					quantity_change: restoredQty,
					quantity_before: 0,
					quantity_after: restoredQty,
					notes: `Undo zero-out for ${item.title}`,
					created_by: user?.id,
				},
			]);
		if (txErr) {
			setError(txErr.message);
			return;
		}

		updateItemStock(item.id, restoredQty);
		if (historyItem?.id === item.id) {
			setHistory((currentHistory) => [
				{
					id: `undo-${Date.now()}`,
					store_item_id: item.id,
					transaction_type: "adjustment",
					quantity_change: restoredQty,
					quantity_before: 0,
					quantity_after: restoredQty,
					notes: `Undo zero-out for ${item.title}`,
					created_at: new Date().toISOString(),
				},
				...currentHistory,
			]);
		}
	};

	const addZeroOutAlert = (item, previousQty) => {
		const alert = {
			id: `${item.id}-${Date.now()}-${Math.random()}`,
			itemId: item.id,
			itemTitle: item.title,
			previousQty,
		};
		setZeroOutAlerts((current) => [...current, alert]);
		window.setTimeout(() => removeZeroOutAlert(alert.id), 5000);
	};

	const updateItemStock = (itemId, nextQty) => {
		setItems((currentItems) =>
			currentItems.map((item) =>
				item.id === itemId ? { ...item, quantity_on_hand: nextQty } : item,
			),
		);
	};

	const applyInventoryAdjustment = async (
		item,
		quantityChange,
		transactionType = "adjustment",
		notes = "",
	) => {
		if (!item) return false;

		const qty = Number(quantityChange);
		if (Number.isNaN(qty)) {
			setError("Enter a valid quantity change (positive or negative).");
			return false;
		}

		const before = Number(item.quantity_on_hand ?? 0);
		const nextQty = Math.max(0, before + qty);
		const { error: updateErr } = await supabase
			.from("store_items")
			.update({ quantity_on_hand: nextQty })
			.eq("id", item.id);
		if (updateErr) {
			setError(updateErr.message);
			return false;
		}

		const {
			data: { user },
		} = await supabase.auth.getUser();
		const { error: txErr } = await supabase
			.from("inventory_transactions")
			.insert([
				{
					store_item_id: item.id,
					transaction_type: transactionType,
					quantity_change: qty,
					quantity_before: before,
					quantity_after: nextQty,
					notes: notes || "",
					created_by: user?.id,
				},
			]);
		if (txErr) {
			setError(txErr.message);
			return false;
		}

		updateItemStock(item.id, nextQty);
		if (historyItem?.id === item.id) {
			setHistory((currentHistory) => [
				{
					id: `temp-${Date.now()}`,
					store_item_id: item.id,
					transaction_type: transactionType,
					quantity_change: qty,
					quantity_before: before,
					quantity_after: nextQty,
					notes: notes || "",
					created_at: new Date().toISOString(),
				},
				...currentHistory,
			]);
		}
		return true;
	};

	const fetchItems = async () => {
		setLoading(true);
		const { data } = await supabase
			.from("store_items")
			.select(
				"id, title, sku, barcode, price_cents, quantity_on_hand, reorder_level, active",
			)
			.order("title");
		setItems(data || []);
		setLoading(false);
	};

	useEffect(() => {
		fetchItems();
	}, []);

	const getDirectionIcon = (key) => {
		if (sortConfig.key !== key) return null;
		return sortConfig.direction === "asc" ? (
			<ArrowUpward fontSize="small" />
		) : (
			<ArrowDownward fontSize="small" />
		);
	};

	const handleSort = (key) => {
		setSortConfig((current) => {
			if (current.key === key) {
				return { key, direction: current.direction === "asc" ? "desc" : "asc" };
			}
			return { key, direction: "asc" };
		});
	};

	const sortableItems = [...items].sort((a, b) => {
		const direction = sortConfig.direction === "asc" ? 1 : -1;
		const aValue = a[sortConfig.key];
		const bValue = b[sortConfig.key];

		if (aValue == null && bValue == null) return 0;
		if (aValue == null) return 1;
		if (bValue == null) return -1;

		if (typeof aValue === "number" && typeof bValue === "number") {
			return (aValue - bValue) * direction;
		}

		return String(aValue).localeCompare(String(bValue)) * direction;
	});

	const filtered = sortableItems.filter(
		(i) =>
			!search ||
			i.title?.toLowerCase().includes(search.toLowerCase()) ||
			i.sku?.toLowerCase().includes(search.toLowerCase()) ||
			i.barcode?.toLowerCase().includes(search.toLowerCase()),
	);

	const openTxDialog = (item) => {
		setSelectedItem(item);
		setTxForm({
			transaction_type: "adjustment",
			quantity_change: "",
			notes: "",
		});
		setError("");
		setTxDialogOpen(true);
	};

	const handleTxSave = async () => {
		if (!selectedItem) return;
		if (!txForm.quantity_change || isNaN(Number(txForm.quantity_change))) {
			setError("Enter a valid quantity change (positive or negative).");
			return;
		}
		setSaving(true);
		setError("");
		const success = await applyInventoryAdjustment(
			selectedItem,
			Number(txForm.quantity_change),
			txForm.transaction_type,
			txForm.notes,
		);
		setSaving(false);
		if (success) {
			setTxDialogOpen(false);
		}
	};

	const handleZeroOut = async (item) => {
		setError("");
		const quantityToRemove = Number(item.quantity_on_hand ?? 0);
		if (quantityToRemove === 0) return;

		const previousQty = Number(item.quantity_on_hand ?? 0);
		const success = await applyInventoryAdjustment(
			item,
			-quantityToRemove,
			"adjustment",
			"Zeroed inventory from the stock dashboard",
		);
		if (success) {
			setHistoryItem(null);
			addZeroOutAlert(item, previousQty);
		}
	};

	const openHistory = async (item) => {
		setHistoryItem(item);
		setHistoryLoading(true);
		const { data } = await supabase
			.from("inventory_transactions")
			.select("*")
			.eq("store_item_id", item.id)
			.order("created_at", { ascending: false })
			.limit(50);
		setHistory(data || []);
		setHistoryLoading(false);
	};

	return (
		<Box>
			<Typography variant="h5" gutterBottom>
				Inventory Management
			</Typography>
			<Box mb={2} maxWidth={400}>
				<TextField
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder="Search by title, SKU, or barcode…"
					size="small"
					fullWidth
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" />
							</InputAdornment>
						),
					}}
				/>
			</Box>

			{loading ? (
				<CircularProgress />
			) : (
				<Box sx={{ overflowX: "auto" }}>
					<Table size="small" sx={{ minWidth: 800 }}>
						<TableHead>
							<TableRow>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === "title"}
										direction={
											sortConfig.key === "title" ? sortConfig.direction : "asc"
										}
										onClick={() => handleSort("title")}
									>
										Title
										{getDirectionIcon("title")}
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === "sku"}
										direction={
											sortConfig.key === "sku" ? sortConfig.direction : "asc"
										}
										onClick={() => handleSort("sku")}
									>
										SKU
										{getDirectionIcon("sku")}
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === "barcode"}
										direction={
											sortConfig.key === "barcode"
												? sortConfig.direction
												: "asc"
										}
										onClick={() => handleSort("barcode")}
									>
										Barcode
										{getDirectionIcon("barcode")}
									</TableSortLabel>
								</TableCell>
								<TableCell align="right">
									<TableSortLabel
										active={sortConfig.key === "price_cents"}
										direction={
											sortConfig.key === "price_cents"
												? sortConfig.direction
												: "asc"
										}
										onClick={() => handleSort("price_cents")}
									>
										Price
										{getDirectionIcon("price_cents")}
									</TableSortLabel>
								</TableCell>
								<TableCell align="center">
									<TableSortLabel
										active={sortConfig.key === "quantity_on_hand"}
										direction={
											sortConfig.key === "quantity_on_hand"
												? sortConfig.direction
												: "asc"
										}
										onClick={() => handleSort("quantity_on_hand")}
									>
										On Hand
										{getDirectionIcon("quantity_on_hand")}
									</TableSortLabel>
								</TableCell>
								<TableCell align="center">
									<TableSortLabel
										active={sortConfig.key === "reorder_level"}
										direction={
											sortConfig.key === "reorder_level"
												? sortConfig.direction
												: "asc"
										}
										onClick={() => handleSort("reorder_level")}
									>
										Reorder At
										{getDirectionIcon("reorder_level")}
									</TableSortLabel>
								</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filtered.map((item) => (
								<TableRow key={item.id} sx={{ opacity: item.active ? 1 : 0.5 }}>
									<TableCell>{item.title}</TableCell>
									<TableCell>{item.sku || "—"}</TableCell>
									<TableCell>{item.barcode || "—"}</TableCell>
									<TableCell align="right">
										${(item.price_cents / 100).toFixed(2)}
									</TableCell>
									<TableCell align="center">{item.quantity_on_hand}</TableCell>
									<TableCell align="center">{item.reorder_level}</TableCell>
									<TableCell>
										{stockChip(item.quantity_on_hand, item.reorder_level)}
									</TableCell>
									<TableCell>
										<Button size="small" onClick={() => openTxDialog(item)}>
											Adjust
										</Button>
										<Button
											size="small"
											color="error"
											onClick={() => handleZeroOut(item)}
											disabled={item.quantity_on_hand === 0}
										>
											Zero
										</Button>
										<Button size="small" onClick={() => openHistory(item)}>
											History
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			)}

			{/* Transaction dialog */}
			<Dialog
				open={txDialogOpen}
				onClose={() => setTxDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Record Inventory Transaction</DialogTitle>
				<DialogContent>
					{selectedItem && (
						<Typography variant="body2" color="text.secondary" gutterBottom>
							{selectedItem.title} — current stock:{" "}
							{selectedItem.quantity_on_hand}
						</Typography>
					)}
					<Box mt={1}>
						<Typography variant="subtitle2" gutterBottom>
							Type
						</Typography>
						<Select
							value={txForm.transaction_type}
							onChange={(e) =>
								setTxForm({ ...txForm, transaction_type: e.target.value })
							}
							fullWidth
							size="small"
						>
							{TRANSACTION_TYPES.map((t) => (
								<MenuItem
									key={t}
									value={t}
									sx={{ textTransform: "capitalize" }}
								>
									{t}
								</MenuItem>
							))}
						</Select>
					</Box>
					<TextField
						label="Quantity change (e.g. +10 or -3)"
						value={txForm.quantity_change}
						onChange={(e) =>
							setTxForm({ ...txForm, quantity_change: e.target.value })
						}
						type="number"
						fullWidth
						margin="normal"
						size="small"
						helperText="Use negative numbers to reduce stock"
					/>
					<TextField
						label="Notes (optional)"
						value={txForm.notes}
						onChange={(e) => setTxForm({ ...txForm, notes: e.target.value })}
						fullWidth
						margin="normal"
						size="small"
						multiline
						rows={2}
					/>
					{error && (
						<Alert severity="error" sx={{ mt: 1 }}>
							{error}
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setTxDialogOpen(false)} disabled={saving}>
						Cancel
					</Button>
					<Button onClick={handleTxSave} variant="contained" disabled={saving}>
						{saving ? <CircularProgress size={20} /> : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* History dialog */}
			<Dialog
				open={!!historyItem}
				onClose={() => setHistoryItem(null)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>Transaction History — {historyItem?.title}</DialogTitle>
				<DialogContent>
					{historyLoading ? (
						<CircularProgress />
					) : (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Date</TableCell>
									<TableCell>Type</TableCell>
									<TableCell align="right">Change</TableCell>
									<TableCell align="right">Before</TableCell>
									<TableCell align="right">After</TableCell>
									<TableCell>Notes</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{history.map((tx) => (
									<TableRow key={tx.id}>
										<TableCell>
											{new Date(tx.created_at).toLocaleString()}
										</TableCell>
										<TableCell sx={{ textTransform: "capitalize" }}>
											{tx.transaction_type}
										</TableCell>
										<TableCell
											align="right"
											sx={{
												color:
													tx.quantity_change >= 0
														? "success.main"
														: "error.main",
												fontWeight: "bold",
											}}
										>
											{tx.quantity_change >= 0 ? "+" : ""}
											{tx.quantity_change}
										</TableCell>
										<TableCell align="right">{tx.quantity_before}</TableCell>
										<TableCell align="right">{tx.quantity_after}</TableCell>
										<TableCell>{tx.notes || "—"}</TableCell>
									</TableRow>
								))}
								{history.length === 0 && (
									<TableRow>
										<TableCell colSpan={6}>
											<Typography variant="body2" color="text.secondary">
												No transactions recorded.
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setHistoryItem(null)}>Close</Button>
				</DialogActions>
			</Dialog>

			<Box
				sx={{
					position: "fixed",
					bottom: 16,
					right: 16,
					zIndex: 1400,
					display: "flex",
					flexDirection: "column",
					gap: 1,
					alignItems: "flex-end",
				}}
			>
				{zeroOutAlerts.map((alert) => (
					<Alert
						key={alert.id}
						severity="warning"
						action={
							<Button
								color="inherit"
								size="small"
								onClick={() => undoZeroOut(alert)}
							>
								Undo
							</Button>
						}
						sx={{
							minWidth: 320,
							maxWidth: 420,
							boxShadow: 3,
						}}
					>
						{alert.itemTitle} was zeroed out.{" "}
						{alert.previousQty > 0
							? `Previous stock: ${alert.previousQty}.`
							: "Item was already empty."}
					</Alert>
				))}
			</Box>
		</Box>
	);
}
